import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  buildPipelineCounts,
  type PipelineStatusKey,
} from '@/features/dashboard/components/dashboardStatusDataHelpers';
import {
  formatRangeWindowNoDataText,
  type DashboardRange,
  useDashboardDateRange,
} from '@/features/dashboard/hooks';
import { buildChartDates, buildLineChartSeries } from '@/features/executions/utils/chartData';
import { buildStackedExecutionChartData } from '@/features/executions/utils/stackedExecutionChart';
import { MetricValueCardsRow } from '@/features/metrics/components/MetricValueCardsRow';
import { buildMetricValueItems } from '@/features/metrics/components/metricValueItems';
import {
  buildStatusDeltaMap,
  buildStatusMetricCards,
  mapStatusOptionsToMetricOptions,
  type MetricCardOption,
  resolveMetricTone,
} from '@/features/metrics/components/statusMetricCards';
import { StatusMetricCardsRow } from '@/features/metrics/components/StatusMetricCardsRow';
import { useMetricsQuery } from '@/features/metrics/hooks/metrics';
import { buildThresholdGuideLines } from '@/features/metrics/lib/chartThresholdGuides';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { PipelinesClonePanel } from '@/features/pipelines/components/PipelinesClonePanel';
import { PipelinesDataPanels } from '@/features/pipelines/components/PipelinesDataPanels';
import { PipelinesLoadingFallback } from '@/features/pipelines/components/PipelinesLoadingFallback';
import { PipelinesMetricsChartPanel } from '@/features/pipelines/components/PipelinesMetricsChartPanel';
import { queryPipelines, queryPipelineSummary } from '@/features/pipelines/data';
import { useProjectFilter, useProjectMetricThresholds } from '@/features/projects';
import { SearchTagsDateHeaderFilters } from '@/features/shared/components/SearchTagsDateHeaderFilters';
import { buildStatusOptions } from '@/shared/constants/statusOptions';
import {
  gridPaths,
  queryKeys,
  useAccessToken,
  usePersistedStringArray,
  useResolvedFeatureFlags,
  useTagOptions,
} from '@/shared/hooks';
import { DelayedSkeletonGate } from '@/ui/composites';
import {
  buildContainsFilter,
  buildDateRangeFilters,
  buildEqFilter,
  buildInFilter,
  buildInFilterWithNull,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { ListPageTemplate } from '@/ui/composites/scaffolds';
import { Stack } from '@/ui/layout/Stack';
import { Text } from '@/ui/primitives';
import type { ExecutionStatusKey, MetricsRange } from '@/features/metrics/metricsHelpers';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { BuildStatus, PipelineListItemDto, QueryResult } from '@flowqa/shared';
import type { ReactElement } from 'react';

export const PipelinesPage = (): ReactElement => {
  const tabsContentGap = 'md' as const;
  const [range, setRange] = useState<DashboardRange>('latest');
  const [activeTab, setActiveTab] = useState<'pipelines' | 'builds'>('pipelines');
  const [chartTab, setChartTab] = useState<'status' | 'success-rate' | 'frequency' | 'duration'>(
    'status',
  );
  const [suppressChartAnimation, setSuppressChartAnimation] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<PipelineStatusKey[]>([]);
  const [selectedTags, setSelectedTags] = usePersistedStringArray('tag-filter:pipelines');
  const [selectedStartDay, setSelectedStartDay] = useState<string | null>(null);
  const [selectedEndDay, setSelectedEndDay] = useState<string | null>(null);
  const [chartSelectedStartDay, setChartSelectedStartDay] = useState<string | null>(null);
  const [chartSelectedEndDay, setChartSelectedEndDay] = useState<string | null>(null);
  const { rangeStartIso, rangeEndIso, previousRangeStartIso, previousRangeEndIso } =
    useDashboardDateRange(range);
  const isLatestMode = range === 'latest';
  const isAllMode = range === 'all';
  const { flags: featureFlags } = useResolvedFeatureFlags();
  const deltasEnabled = featureFlags?.deltas ?? true;
  const cloneTableEnabled = featureFlags?.pipelinesCloneTable ?? false;
  const showDelta = deltasEnabled && !isLatestMode && !isAllMode && previousRangeStartIso !== null;
  const accessToken = useAccessToken();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const { tagOptions, loading: tagOptionsLoading } = useTagOptions({
    projectId: selectedProjectId,
    enabled: Boolean(accessToken),
  });
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const statusFilterOptions = useMemo(
    () => [...buildStatusOptions, { value: 'none' as const, label: 'No Builds' }],
    [],
  );
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 300);
    return () => {
      window.clearTimeout(timer);
    };
  }, [searchValue]);
  useEffect(() => {
    setSuppressChartAnimation(true);
    const timer = window.setTimeout(() => {
      setSuppressChartAnimation(false);
    }, 0);
    return () => {
      window.clearTimeout(timer);
    };
  }, [chartTab]);
  const tagFilters = useMemo(() => buildInFilter('tags', selectedTags), [selectedTags]);
  const pipelineSearchFilters = useMemo(
    () => mergeFilters(buildContainsFilter('name', debouncedSearchValue), tagFilters),
    [debouncedSearchValue, tagFilters],
  );
  const buildSearchFilters = useMemo(
    () => buildContainsFilter('pipelineName', debouncedSearchValue),
    [debouncedSearchValue],
  );
  const dateRangeFilters = useMemo(
    () => buildDateRangeFilters('startedAt', rangeStartIso, rangeEndIso),
    [rangeEndIso, rangeStartIso],
  );
  const selectedDayFilters = useMemo(() => {
    if (!selectedStartDay && !selectedEndDay) return [];
    const start = selectedStartDay ? `${selectedStartDay}T00:00:00.000Z` : null;
    const end = selectedEndDay ? `${selectedEndDay}T23:59:59.999Z` : null;
    return buildDateRangeFilters('startedAt', start, end);
  }, [selectedEndDay, selectedStartDay]);
  const pipelineStatusFilters = useMemo(() => {
    if (selectedStatuses.length === 0) return [];
    const hasNoBuilds = selectedStatuses.includes('none');
    const statusValues = selectedStatuses.filter(
      (status): status is BuildStatus => status !== 'none',
    );
    const filterValues: Array<BuildStatus | null> = hasNoBuilds
      ? [null, ...statusValues]
      : statusValues;
    return buildInFilterWithNull('latestBuildStatus', filterValues);
  }, [selectedStatuses]);
  const selectedBuildStatuses = useMemo(
    () => selectedStatuses.filter((status): status is BuildStatus => status !== 'none'),
    [selectedStatuses],
  );
  const selectedChartStatuses = useMemo<ExecutionStatusKey[]>(() => {
    const mapped = selectedStatuses
      .map((status): ExecutionStatusKey | null => {
        if (status === 'completed') return 'passed';
        if (status === 'failed') return 'failed';
        return null;
      })
      .filter((status): status is ExecutionStatusKey => status !== null);
    return Array.from(new Set(mapped));
  }, [selectedStatuses]);
  const hasUnsupportedChartStatusSelection =
    selectedStatuses.length > 0 && selectedChartStatuses.length === 0;
  const isNoBuildsOnly = selectedStatuses.length === 1 && selectedStatuses[0] === 'none';
  const buildStatusFilters = useMemo(() => {
    if (selectedStatuses.length === 0) return [];
    if (isNoBuildsOnly) return buildEqFilter('id', 'no-match');
    return buildInFilter('status', selectedBuildStatuses);
  }, [isNoBuildsOnly, selectedBuildStatuses, selectedStatuses.length]);
  const buildTableFilters = useMemo(
    () =>
      mergeFilters(
        selectedDayFilters,
        !isLatestMode && !isAllMode ? dateRangeFilters : undefined,
        buildSearchFilters,
        buildStatusFilters,
        tagFilters,
      ),
    [
      buildStatusFilters,
      buildSearchFilters,
      dateRangeFilters,
      isAllMode,
      isLatestMode,
      selectedDayFilters,
      tagFilters,
    ],
  );
  const summaryInput = useMemo(
    () => ({
      page: 1,
      pageSize: 1,
      sorts: [],
      filters: pipelineSearchFilters,
      metricsStartDay: selectedStartDay ?? rangeStartIso?.slice(0, 10) ?? undefined,
      metricsEndDay: selectedEndDay ?? rangeEndIso?.slice(0, 10) ?? undefined,
    }),
    [pipelineSearchFilters, rangeEndIso, rangeStartIso, selectedEndDay, selectedStartDay],
  );
  const previousSummaryInput = useMemo(
    () =>
      isLatestMode
        ? null
        : {
            page: 1,
            pageSize: 1,
            sorts: [],
            filters: pipelineSearchFilters,
            metricsStartDay: selectedStartDay
              ? undefined
              : (previousRangeStartIso?.slice(0, 10) ?? undefined),
            metricsEndDay: selectedEndDay
              ? undefined
              : (previousRangeEndIso?.slice(0, 10) ?? undefined),
          },
    [
      isLatestMode,
      pipelineSearchFilters,
      previousRangeEndIso,
      previousRangeStartIso,
      selectedEndDay,
      selectedStartDay,
    ],
  );
  const summaryFiltersKey = useMemo(
    () =>
      JSON.stringify({
        metricsStartDay: summaryInput.metricsStartDay ?? null,
        metricsEndDay: summaryInput.metricsEndDay ?? null,
        filters: summaryInput.filters,
      }),
    [summaryInput.filters, summaryInput.metricsEndDay, summaryInput.metricsStartDay],
  );
  const previousSummaryFiltersKey = useMemo(
    () =>
      previousSummaryInput
        ? JSON.stringify({
            metricsStartDay: previousSummaryInput.metricsStartDay ?? null,
            metricsEndDay: previousSummaryInput.metricsEndDay ?? null,
            filters: previousSummaryInput.filters,
          })
        : 'latest',
    [previousSummaryInput],
  );
  const pipelineSummaryQuery = useQuery({
    queryKey: queryKeys.pipelineSummary(
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
      summaryFiltersKey,
    ),
    enabled: Boolean(accessToken),
    queryFn: async () =>
      queryPipelineSummary(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        summaryInput,
        accessToken,
      ),
  });
  const previousPipelineSummaryQuery = useQuery({
    queryKey: queryKeys.pipelineSummary(
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
      previousSummaryFiltersKey,
    ),
    enabled: Boolean(accessToken) && !isLatestMode,
    queryFn: async () =>
      queryPipelineSummary(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        previousSummaryInput ?? summaryInput,
        accessToken,
      ),
  });
  const pipelineSummary = pipelineSummaryQuery.data ?? null;
  const previousPipelineSummary = !isLatestMode
    ? (previousPipelineSummaryQuery.data ?? null)
    : null;
  const pipelineCounts = useMemo(() => buildPipelineCounts(pipelineSummary), [pipelineSummary]);
  const previousPipelineCounts = useMemo(
    () => buildPipelineCounts(previousPipelineSummary),
    [previousPipelineSummary],
  );
  const pipelineDeltas = useMemo(
    () =>
      showDelta && previousPipelineSummary
        ? buildStatusDeltaMap(pipelineCounts, previousPipelineCounts)
        : undefined,
    [pipelineCounts, previousPipelineCounts, previousPipelineSummary, showDelta],
  );
  const pipelineMetricOptions = useMemo<Array<MetricCardOption<PipelineStatusKey>>>(() => {
    const baseOptions = mapStatusOptionsToMetricOptions(buildStatusOptions, resolveMetricTone);
    return [
      ...(baseOptions as Array<MetricCardOption<PipelineStatusKey>>),
      { value: 'none', label: 'No Builds', tone: resolveMetricTone('none') },
    ];
  }, []);
  const pipelineMetricCards = useMemo(
    () =>
      buildStatusMetricCards({
        options: pipelineMetricOptions,
        counts: pipelineCounts,
        deltas: pipelineDeltas,
        total: pipelineSummary?.total,
        loading: pipelineSummaryQuery.isLoading,
        showDelta,
      }),
    [
      showDelta,
      pipelineCounts,
      pipelineDeltas,
      pipelineMetricOptions,
      pipelineSummary?.total,
      pipelineSummaryQuery.isLoading,
    ],
  );
  const hasCustomDates = Boolean(selectedStartDay ?? selectedEndDay);
  const pipelinesNoRowsText = formatRangeWindowNoDataText(
    'No pipelines found',
    range,
    hasCustomDates,
  );
  const buildsNoRowsText = formatRangeWindowNoDataText('No builds found', range, hasCustomDates);
  useEffect(() => {
    if (!isLatestMode) {
      setChartSelectedStartDay(null);
      setChartSelectedEndDay(null);
    }
  }, [isLatestMode]);
  const metricsRange = useMemo<MetricsRange>(
    () => (hasCustomDates ? 'all' : isLatestMode ? 'all' : range),
    [hasCustomDates, isLatestMode, range],
  );
  const previousStartDay = previousRangeStartIso?.slice(0, 10) ?? null;
  const previousEndDay = previousRangeEndIso?.slice(0, 10) ?? null;
  const buildMetricsQuery = useMetricsQuery({
    range: metricsRange,
    statuses: selectedChartStatuses,
    tags: selectedTags,
    scope: 'pipeline',
    projectId: selectedProjectId,
    latestOnly: isLatestMode,
    name: debouncedSearchValue || null,
    startDay: isLatestMode ? null : hasCustomDates ? selectedStartDay : null,
    endDay: isLatestMode ? null : hasCustomDates ? selectedEndDay : null,
    enabled: Boolean(accessToken),
    refetchOnWindowFocus: false,
  });
  const previousMetricsQuery = useMetricsQuery({
    range: 'all',
    statuses: selectedChartStatuses,
    tags: selectedTags,
    scope: 'pipeline',
    projectId: selectedProjectId,
    name: debouncedSearchValue || null,
    startDay: showDelta ? previousStartDay : null,
    endDay: showDelta ? previousEndDay : null,
    enabled: Boolean(accessToken && showDelta),
    refetchOnWindowFocus: false,
  });
  const summaryPassRate = pipelineSummary?.averageExecutionPassRate ?? null;
  const previousSummaryPassRate = showDelta
    ? (previousPipelineSummary?.averageExecutionPassRate ?? null)
    : null;
  const metricValueItems = useMemo(
    () =>
      buildMetricValueItems({
        metrics: buildMetricsQuery.data ?? null,
        previousMetrics: showDelta ? (previousMetricsQuery.data ?? null) : null,
        passRateOverride: summaryPassRate,
        previousPassRateOverride: previousSummaryPassRate,
        thresholds: thresholds.pipelines,
        showDelta,
      }),
    [
      buildMetricsQuery.data,
      previousMetricsQuery.data,
      previousSummaryPassRate,
      showDelta,
      summaryPassRate,
      thresholds.pipelines,
    ],
  );
  const stackedChartData = useMemo(() => {
    const baseData = buildStackedExecutionChartData(buildMetricsQuery.data);
    if (!baseData) return null;
    if (hasUnsupportedChartStatusSelection) {
      const emptyData = baseData.data.map((datum) => ({
        ...datum,
        passed: 0,
        failed: 0,
        errored: 0,
        skipped: 0,
      }));
      return { ...baseData, data: emptyData };
    }
    const series = baseData.series.map((item) =>
      item.key === 'passed' ? { ...item, label: 'Completed' } : item,
    );
    return { ...baseData, series };
  }, [buildMetricsQuery.data, hasUnsupportedChartStatusSelection]);
  const chartDates = useMemo(() => {
    const labels = buildMetricsQuery.data?.metricsRangeLabels ?? [];
    const dates = buildMetricsQuery.data?.metricsRangeDates ?? [];
    return buildChartDates(labels, dates);
  }, [buildMetricsQuery.data?.metricsRangeDates, buildMetricsQuery.data?.metricsRangeLabels]);
  const chartFilterRange = useMemo(() => {
    if (!isLatestMode || !chartSelectedStartDay) return null;
    return {
      start: chartSelectedStartDay,
      end: chartSelectedEndDay ?? chartSelectedStartDay,
    };
  }, [chartSelectedEndDay, chartSelectedStartDay, isLatestMode]);
  const chartFilterIndices = useMemo(() => {
    if (!chartFilterRange) return null;
    const { start, end } = chartFilterRange;
    return chartDates
      .map((date, index) => (date >= start && date <= end ? index : -1))
      .filter((index) => index >= 0);
  }, [chartDates, chartFilterRange]);
  const filteredStackedChartData = useMemo(() => {
    if (!stackedChartData || !chartFilterIndices) return stackedChartData;
    const filteredData = chartFilterIndices
      .map((index) => stackedChartData.data[index])
      .filter(Boolean);
    return { ...stackedChartData, data: filteredData };
  }, [chartFilterIndices, stackedChartData]);
  const lineChartLabels = buildMetricsQuery.data?.metricsRangeLabels ?? [];
  const successRateChartData = useMemo(() => {
    if (hasUnsupportedChartStatusSelection) return [];
    const series = buildLineChartSeries(
      buildMetricsQuery.data?.series.filteredPassRate,
      lineChartLabels,
      chartDates,
      'passRate',
    );
    if (series) return series;
    return buildMetricsQuery.isFetched ? [] : null;
  }, [
    hasUnsupportedChartStatusSelection,
    buildMetricsQuery.data?.series.filteredPassRate,
    buildMetricsQuery.isFetched,
    chartDates,
    lineChartLabels,
  ]);
  const frequencyChartData = useMemo(() => {
    if (hasUnsupportedChartStatusSelection) return [];
    const series = buildLineChartSeries(
      buildMetricsQuery.data?.series.executionTrendMetrics,
      lineChartLabels,
      chartDates,
      'frequency',
    );
    if (series) return series;
    return buildMetricsQuery.isFetched ? [] : null;
  }, [
    hasUnsupportedChartStatusSelection,
    buildMetricsQuery.data?.series.executionTrendMetrics,
    buildMetricsQuery.isFetched,
    chartDates,
    lineChartLabels,
  ]);
  const durationChartData = useMemo(() => {
    if (hasUnsupportedChartStatusSelection) return [];
    const series = buildLineChartSeries(
      buildMetricsQuery.data?.series.executionDurationAverage,
      lineChartLabels,
      chartDates,
      'duration',
    );
    if (series) return series;
    return buildMetricsQuery.isFetched ? [] : null;
  }, [
    hasUnsupportedChartStatusSelection,
    buildMetricsQuery.data?.series.executionDurationAverage,
    buildMetricsQuery.isFetched,
    chartDates,
    lineChartLabels,
  ]);
  const filteredSuccessRateChartData = useMemo(() => {
    if (!successRateChartData || !chartFilterIndices) return successRateChartData;
    return chartFilterIndices.map((index) => successRateChartData[index]).filter(Boolean);
  }, [chartFilterIndices, successRateChartData]);
  const filteredFrequencyChartData = useMemo(() => {
    if (!frequencyChartData || !chartFilterIndices) return frequencyChartData;
    return chartFilterIndices.map((index) => frequencyChartData[index]).filter(Boolean);
  }, [chartFilterIndices, frequencyChartData]);
  const filteredDurationChartData = useMemo(() => {
    if (!durationChartData || !chartFilterIndices) return durationChartData;
    return chartFilterIndices.map((index) => durationChartData[index]).filter(Boolean);
  }, [chartFilterIndices, durationChartData]);
  const successRateGuideLines = useMemo(
    () => buildThresholdGuideLines(thresholds.pipelines.passRate),
    [thresholds.pipelines.passRate],
  );
  const frequencyGuideLines = useMemo(
    () => buildThresholdGuideLines(thresholds.pipelines.frequency),
    [thresholds.pipelines.frequency],
  );
  const durationGuideLines = useMemo(
    () => buildThresholdGuideLines(thresholds.pipelines.avgDuration),
    [thresholds.pipelines.avgDuration],
  );
  const showLineChartPoints = useMemo(() => {
    const start = isLatestMode ? chartSelectedStartDay : selectedStartDay;
    const end = isLatestMode ? (chartSelectedEndDay ?? chartSelectedStartDay) : selectedEndDay;
    return Boolean(start && end && start === end);
  }, [chartSelectedEndDay, chartSelectedStartDay, isLatestMode, selectedEndDay, selectedStartDay]);
  const metricsStartDay = useMemo(() => chartDates[0] ?? null, [chartDates]);
  const metricsEndDay = useMemo(
    () => (chartDates.length > 0 ? (chartDates[chartDates.length - 1] ?? null) : null),
    [chartDates],
  );
  const pickerStartDay = useMemo(
    () =>
      selectedStartDay ??
      (range !== 'latest' && range !== 'all'
        ? (rangeStartIso?.slice(0, 10) ?? null)
        : metricsStartDay),
    [metricsStartDay, range, rangeStartIso, selectedStartDay],
  );
  const pickerEndDay = useMemo(
    () =>
      selectedEndDay ??
      (range !== 'latest' && range !== 'all' ? (rangeEndIso?.slice(0, 10) ?? null) : metricsEndDay),
    [metricsEndDay, range, rangeEndIso, selectedEndDay],
  );
  const handleStartDayChange = useCallback(
    (nextDay: string | null) => {
      setSelectedStartDay(nextDay);
      if (!nextDay) {
        setSelectedEndDay(null);
        return;
      }
      if (!selectedEndDay) {
        setSelectedEndDay(pickerEndDay ?? nextDay);
      }
    },
    [pickerEndDay, selectedEndDay],
  );
  const handleEndDayChange = useCallback(
    (nextDay: string | null) => {
      setSelectedEndDay(nextDay);
      if (!nextDay) {
        setSelectedStartDay(null);
        return;
      }
      if (!selectedStartDay) {
        setSelectedStartDay(pickerStartDay ?? nextDay);
      }
    },
    [pickerStartDay, selectedStartDay],
  );
  const hasChartDateFilter = Boolean(selectedStartDay ?? selectedEndDay);
  const handleChartDateClear = useCallback(() => {
    setChartSelectedStartDay(null);
    setChartSelectedEndDay(null);
    setSelectedStartDay(null);
    setSelectedEndDay(null);
  }, []);
  const handleChartSelect = useCallback(
    (dataIndex: number) => {
      const day = chartDates[dataIndex];
      if (!day) return;
      if (isLatestMode) {
        if (chartSelectedStartDay) {
          setChartSelectedStartDay(null);
          setChartSelectedEndDay(null);
          setSelectedStartDay(null);
          setSelectedEndDay(null);
        } else {
          setChartSelectedStartDay(day);
          setChartSelectedEndDay(day);
          setSelectedStartDay(day);
          setSelectedEndDay(day);
        }
      } else {
        const isSameDay = selectedStartDay === day && selectedEndDay === day;
        if (isSameDay) {
          setSelectedStartDay(null);
          setSelectedEndDay(null);
        } else {
          setSelectedStartDay(day);
          setSelectedEndDay(day);
        }
      }
    },
    [
      chartDates,
      chartSelectedEndDay,
      chartSelectedStartDay,
      isLatestMode,
      selectedEndDay,
      selectedStartDay,
    ],
  );
  const chartHeight = 360;
  const showPageSkeleton =
    Boolean(accessToken) && pipelineSummaryQuery.isLoading && !pipelineSummary;
  const pipelinesQueryKey = useMemo(
    () =>
      queryKeys.dataGrid(
        gridPaths.pipelines,
        rangeStartIso,
        rangeEndIso,
        previousRangeStartIso,
        previousRangeEndIso,
      ),
    [previousRangeEndIso, previousRangeStartIso, rangeEndIso, rangeStartIso],
  );
  const pipelinesQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<PipelineListItemDto>> =>
      queryPipelines(selectedProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        metricsStartDay: selectedStartDay ?? rangeStartIso?.slice(0, 10) ?? undefined,
        metricsEndDay: selectedEndDay ?? rangeEndIso?.slice(0, 10) ?? undefined,
        previousMetricsStartDay: selectedStartDay
          ? undefined
          : (previousRangeStartIso?.slice(0, 10) ?? undefined),
        previousMetricsEndDay: selectedEndDay
          ? undefined
          : (previousRangeEndIso?.slice(0, 10) ?? undefined),
        include: {
          latestBuild: true,
          metrics: true,
          metricDeltas: deltasEnabled && !isLatestMode && !isAllMode,
        },
      }),
    [
      previousRangeEndIso,
      previousRangeStartIso,
      rangeEndIso,
      rangeStartIso,
      deltasEnabled,
      isAllMode,
      isLatestMode,
      selectedOrganisationId,
      selectedProjectId,
      selectedEndDay,
      selectedStartDay,
    ],
  );
  return (
    <DelayedSkeletonGate show={showPageSkeleton} fallback={<PipelinesLoadingFallback />}>
      <ListPageTemplate
        headerActionsMode="separate"
        title={
          <Text role="sectionTitle" component="h1">
            Pipelines
          </Text>
        }
        headerActions={
          <SearchTagsDateHeaderFilters
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            searchAriaLabel="Search pipelines"
            selectedStatuses={selectedStatuses}
            onStatusesChange={(nextStatuses) => {
              setSelectedStatuses(nextStatuses as PipelineStatusKey[]);
            }}
            statusOptions={statusFilterOptions}
            selectedTags={selectedTags}
            onTagsChange={setSelectedTags}
            tagOptions={tagOptions}
            tagOptionsLoading={tagOptionsLoading}
            range={range}
            onRangeChange={(nextRange) => {
              setRange(nextRange);
              setSelectedStartDay(null);
              setSelectedEndDay(null);
            }}
            pickerStartDay={pickerStartDay}
            pickerEndDay={pickerEndDay}
            hasCustomDates={hasCustomDates}
            isLatestMode={isLatestMode}
            onSelectedStartDayChange={handleStartDayChange}
            onSelectedEndDayChange={handleEndDayChange}
          />
        }
        summary={
          <Stack gap="lg">
            <StatusMetricCardsRow
              cards={pipelineMetricCards}
              selectedIds={selectedStatuses}
              onToggle={(status) => {
                if (status === 'total') return;
                setSelectedStatuses((prev) =>
                  prev.includes(status as PipelineStatusKey)
                    ? prev.filter((item) => item !== status)
                    : [...prev, status as PipelineStatusKey],
                );
              }}
            />
            <MetricValueCardsRow items={metricValueItems} />
          </Stack>
        }
        charts={
          <PipelinesMetricsChartPanel
            tabsContentGap={tabsContentGap}
            chartTab={chartTab}
            onChartTabChange={setChartTab}
            hasChartDateFilter={hasChartDateFilter}
            onChartDateClear={handleChartDateClear}
            chartHeight={chartHeight}
            suppressChartAnimation={suppressChartAnimation}
            filteredStackedChartData={filteredStackedChartData}
            filteredSuccessRateChartData={filteredSuccessRateChartData}
            filteredFrequencyChartData={filteredFrequencyChartData}
            filteredDurationChartData={filteredDurationChartData}
            successRateGuideLines={successRateGuideLines}
            frequencyGuideLines={frequencyGuideLines}
            durationGuideLines={durationGuideLines}
            onChartSelect={handleChartSelect}
            showLineChartPoints={showLineChartPoints}
            emptyStateTitle={buildsNoRowsText}
          />
        }
        content={
          <Stack gap="md">
            <PipelinesDataPanels
              tabsContentGap={tabsContentGap}
              activeTab={activeTab}
              onActiveTabChange={setActiveTab}
              deltasEnabled={deltasEnabled}
              isLatestMode={isLatestMode}
              isAllMode={isAllMode}
              pipelinesQueryKey={pipelinesQueryKey}
              pipelinesQueryFn={pipelinesQueryFn}
              pipelineSearchFilters={pipelineSearchFilters}
              pipelineStatusFilters={pipelineStatusFilters}
              buildTableFilters={buildTableFilters}
              pipelinesNoRowsText={pipelinesNoRowsText}
              buildsNoRowsText={buildsNoRowsText}
            />
            <PipelinesClonePanel
              visible={cloneTableEnabled && activeTab === 'pipelines'}
              selectedProjectId={selectedProjectId ?? null}
              selectedOrganisationId={selectedOrganisationId ?? null}
              deltasEnabled={deltasEnabled}
            />
          </Stack>
        }
      />
    </DelayedSkeletonGate>
  );
};
